import React from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";

const BrandForm = ({
  formData,
  setFormData,
  selectedImage,
  handleImageSelect,
  previewUrl,
  setPreviewUrl,
  setSelectedImage,
}) => {
  return (
    <div className="grid grid-cols-2 gap-6">
      <TextField
        label="Brand Name"
        variant="outlined"
        size="small"
        fullWidth
        required
        value={formData.name}
        onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
      />

      <FormControl size="small" fullWidth>
        <InputLabel>Brand Type</InputLabel>
        <Select
          value={formData.brandtype}
          label="Brand Type"
          onChange={e =>
            setFormData(prev => ({ ...prev, brandtype: e.target.value }))
          }>
          <MenuItem value="DOMESTIC">Domestic</MenuItem>
          <MenuItem value="INTERNATIONAL">International</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Description"
        variant="outlined"
        size="small"
        multiline
        rows={3}
        fullWidth
        className="col-span-2"
        value={formData.description}
        onChange={e =>
          setFormData(prev => ({ ...prev, description: e.target.value }))
        }
      />

      <FormControl size="small" fullWidth>
        <InputLabel>Order Validation Type</InputLabel>
        <Select
          value={formData.orderValidation?.validationType}
          label="Order Validation Type"
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              orderValidation: {
                ...prev.orderValidation,
                validationType: e.target.value,
              },
            }))
          }>
          <MenuItem value="NONE">None</MenuItem>
          <MenuItem value="MOQ_MOV">MOQ/MOV</MenuItem>
        </Select>
      </FormControl>

      {formData.orderValidation?.validationType === "MOQ_MOV" && (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>Validation Condition</InputLabel>
            <Select
              value={formData.orderValidation?.validationCondition}
              label="Validation Condition"
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  orderValidation: {
                    ...prev.orderValidation,
                    validationCondition: e.target.value,
                  },
                }))
              }>
              <MenuItem value="ANY_ONE">Any One Condition</MenuItem>
              <MenuItem value="BOTH">Both Conditions</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Minimum Order Quantity"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={formData.orderValidation?.minOrderQty || ""}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                orderValidation: {
                  ...prev.orderValidation,
                  minOrderQty: Number(e.target.value),
                },
              }))
            }
          />

          <TextField
            label="Minimum Order Value"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={formData.orderValidation?.minOrderValue || ""}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                orderValidation: {
                  ...prev.orderValidation,
                  minOrderValue: Number(e.target.value),
                },
              }))
            }
          />
        </>
      )}

      {/* Image Upload Section */}
      <div className="col-span-2">
        <p className="text-sm font-medium text-gray-700 mb-3">Brand Logo</p>
        <div className="flex items-start gap-6">
          {/* Image Preview */}
          <div className="w-32 h-32 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Brand Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center p-4">
                <img
                  src={require("../../../../../assets/upload.png")}
                  alt="upload"
                  className="w-12 h-12 mx-auto mb-2 opacity-50"
                />
                <p className="text-xs text-gray-500">No image selected</p>
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-1">
            <div className="flex flex-col gap-3">
              <div
                className={`border-2 border-dashed rounded-lg transition-colors ${
                  previewUrl
                    ? "border-green-200 bg-green-50"
                    : "border-gray-200 hover:border-[#5652B0]"
                }`}>
                <label className="flex flex-col items-center justify-center py-4 px-6 cursor-pointer">
                  <div className="flex flex-col items-center">
                    {previewUrl ? (
                      <>
                        <div className="p-2 rounded-full bg-green-100 mb-2">
                          <img
                            src={require("../../../../../assets/upload.png")}
                            alt="upload"
                            className="w-5 h-5"
                          />
                        </div>
                        <p className="text-sm font-medium text-green-600">
                          Image selected
                        </p>
                        <p className="text-xs text-green-500 mt-1">
                          Click to change image
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="p-2 rounded-full bg-gray-100 mb-2">
                          <img
                            src={require("../../../../../assets/upload.png")}
                            alt="upload"
                            className="w-5 h-5"
                          />
                        </div>
                        <p className="text-sm font-medium text-gray-700">
                          Drop your image here, or{" "}
                          <span className="text-[#5652B0]">browse</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          JPG, PNG up to 5MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleImageSelect}
                  />
                </label>
              </div>

              {/* Image Requirements */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Image Requirements:
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-center">
                    <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                    Square aspect ratio (1:1) recommended
                  </li>
                  <li className="flex items-center">
                    <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                    Minimum resolution: 512x512 pixels
                  </li>
                  <li className="flex items-center">
                    <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                    Maximum file size: 5MB
                  </li>
                </ul>
              </div>

              {/* Remove Image Button */}
              {previewUrl && (
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, image: null }));
                    setSelectedImage(null);
                    setPreviewUrl(null);
                  }}
                  className="text-sm text-red-600 hover:text-red-700 flex items-center justify-center py-1">
                  Remove image
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandForm;
